import { Button, Hint, Link, Modal } from "react-ui";
import * as React from "react";
import { FileUploader, FileUploaderAttachedFile } from "@skbkontur/react-ui";

import { FileProperties } from "../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { UploadedFileInfo, UploadedFileStatus } from "../../../../Engine/ActionCreators/FufUploadActionCreator";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { UPLOAD_FUF } from "../../../metricsAction";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { CustomFile } from "../../../../Helpers/file";
import { stringifyUnknownError } from "../../../../../../Common/TypingUtils";
import { FufSizeTooLargeError } from "../../../../../../Common/CandyApiClient/Types/DraftActions/FufSizeTooLargeError";
import { Icon } from "../../../Icons/Icon";

import {
    Column1,
    Column2,
    Container,
    Content,
    ErrorContainer,
    ErrorText,
    FileTable,
    FormFileDemandDesc,
    FormFileProperties,
    FormName,
    FufUploadAlertLayout,
    FufUploadAlertText,
} from "./FufUploadModal.styles";

export interface FufUploadModalProps {
    onClose: () => void;
    setFuf: (driveUri: string) => Promise<void>;
    reloadForm: (useServerSideFLangNormalization?: boolean) => Promise<void>;
    uploadFuf: (file: CustomFile | undefined, gfv: string) => Promise<UploadedFileInfo>;
    currentGfv: string;
    getFufExpectedDemandFileName: () => Promise<string | undefined>;
    uploadFufLightboxHideDemandNameButton: boolean;
    fufSizeTooLargeErrorText?: string | React.ReactNode;
    uploadFufLightboxTitle?: string;
    uploadFufLightboxAlert?: string;
    useServerSideFLangNormalization?: boolean;
}

export function FufFileProperties(props: {
    fileProperties: FileProperties;
    uploadFufLightboxHideDemandNameButton: boolean;
    expectedDemandFileName?: string;
}): JSX.Element {
    const [hideFileInfo, setHideFileInfo] = React.useState<boolean>(true);

    const cropPayerName = (payerName: string) => (payerName.length > 150 ? `${payerName.slice(0, 100)}...` : payerName);

    const fileProperties = props.fileProperties;
    const expectedDemandFileName = props.expectedDemandFileName;
    const renderInnKpp = fileProperties.INN && fileProperties.KPP;
    const noInfoText = "Информация отсутствует";

    return (
        <FormFileProperties>
            <FormName data-tid="FormName">{fileProperties.FormName ?? "Неизвестная форма"}</FormName>
            {renderInnKpp && (
                <>
                    {fileProperties.INN} - {fileProperties.KPP},{" "}
                </>
            )}
            {fileProperties.PayerName && (
                <Hint text={fileProperties.PayerName} pos="right top">
                    {cropPayerName(fileProperties.PayerName)}
                </Hint>
            )}

            {!props.uploadFufLightboxHideDemandNameButton && hideFileInfo && (
                <Link data-tid="HideDemandInfoLink" onClick={() => setHideFileInfo(false)}>
                    Показать имя требования
                </Link>
            )}

            {!hideFileInfo && fileProperties != undefined && (
                <div>
                    <div>
                        <Link onClick={() => setHideFileInfo(true)}>Свернуть имя требования</Link>
                    </div>
                    <FileTable>
                        <Column1>В Файле</Column1>
                        <Column2>{fileProperties.Demandfilename || noInfoText}</Column2>
                        <Column1>В Системе</Column1>
                        <Column2>{expectedDemandFileName || noInfoText}</Column2>
                    </FileTable>
                    <FormFileDemandDesc>
                        Если имена не совпадают, проверьте на какое требование вы формируете ответ. Если вы выбрали
                        нужное требование, не беспокойтесь, Экстерн правильно заполнит имя по формату реестра.
                    </FormFileDemandDesc>
                </div>
            )}
        </FormFileProperties>
    );
}

export function FufUploadError(props: { fileName?: string; messageWithMarkup?: React.ReactNode }): JSX.Element {
    return (
        <ErrorContainer>
            <ErrorText>Произошла ошибка при загрузке файла.</ErrorText>
            <ErrorText>{props.messageWithMarkup}</ErrorText>
        </ErrorContainer>
    );
}

export function FufFileName(props: { fileName: string }): JSX.Element {
    return (
        <div>
            <Icon name={"DocIcon"} />
            <>{props.fileName}</>
        </div>
    );
}

const FufUploadAlert = (props: { text: string }) => (
    <FufUploadAlertLayout>
        <Icon name={"InfoCircleIcon"} size={16} />
        <FufUploadAlertText>{props.text}</FufUploadAlertText>
    </FufUploadAlertLayout>
);

interface NodeTextErrorPropsType {
    error: FufSizeTooLargeError | unknown;
    fufSizeTooLargeErrorText: React.ReactNode;
}

function NodeTextError(props: NodeTextErrorPropsType) {
    const setDotToEndIfNeeded = (text?: string) =>
        text != undefined && text.length > 0 && text[text.length - 1] != "." ? text + "." : text;

    const isFufSizeTooLargeError = props.error instanceof FufSizeTooLargeError;

    const nodeTextError = (
        <div>
            {setDotToEndIfNeeded(stringifyUnknownError(props.error))}
            <br />
            {isFufSizeTooLargeError && props.fufSizeTooLargeErrorText}
        </div>
    );
    return nodeTextError;
}

export function FufUploadModal(props: FufUploadModalProps): JSX.Element {
    const [uploadFufStatus, setUploadFufStatus] = React.useState(UploadedFileStatus.Cancel);
    const [fileName, setFileName] = React.useState<string | undefined>(undefined);
    const [expectedDemandFileName, setExpectedDemandFileName] = React.useState<string | undefined>(undefined);
    const [fileProperties, setFileProperties] = React.useState<FileProperties | undefined>(undefined);
    const [loadError, setLoadError] = React.useState<undefined | React.ReactNode>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const track = useMetrics();

    const wrapAsyncFunc = async (asyncFunc: () => Promise<void>): Promise<void> => {
        try {
            await asyncFunc();
        } catch (error) {
            const nodeTextError = (
                <NodeTextError error={error} fufSizeTooLargeErrorText={props.fufSizeTooLargeErrorText} />
            );
            setLoadError(nodeTextError);
            setUploadFufStatus(UploadedFileStatus.Unresolved);
        } finally {
            setIsLoading(false);
        }
    };

    const uploadForEdit = async (): Promise<void> => {
        if (fileProperties?.fufUri != undefined) {
            setIsLoading(true);
            await wrapAsyncFunc(async () => {
                await props.setFuf(fileProperties.fufUri);
                await props.reloadForm(props.useServerSideFLangNormalization);
                await onClose();
            });
        }
    };

    const clearAndUploadFuf = async (uploadFile: FileUploaderAttachedFile): Promise<void> => {
        setFileProperties(undefined);
        await uploadFuf(uploadFile);
    };

    const uploadFuf = async (file: FileUploaderAttachedFile): Promise<void> => {
        await wrapAsyncFunc(async () => {
            setLoadError(undefined);
            setFileName(undefined);
            const uploadFile = file?.originalFile;
            if (uploadFile?.name != fileName) {
                setFileName(uploadFile?.name);
            }
            const uploadResult = await props.uploadFuf(uploadFile, props.currentGfv);
            setUploadFufStatus(uploadResult.uploadStatus);
            if (uploadResult.fileName != undefined) {
                track(UPLOAD_FUF, {
                    status: uploadResult.uploadStatus,
                    fileExtension:
                        uploadResult.fileName.substring(
                            uploadResult.fileName.lastIndexOf(".") + 1,
                            uploadResult.fileName.length
                        ) || uploadResult.fileName,
                });
            }
            setFileProperties(uploadResult.fileProperties);
            setUploadFufStatus(uploadResult.uploadStatus);
        });
    };

    const onClose = async () => {
        setFileProperties(undefined);
        props.onClose();
    };

    const onClear = async () => {
        setFileProperties(undefined);
        setUploadFufStatus(UploadedFileStatus.Cancel);
    };

    useAsyncEffect(async () => {
        const expectedDemandFilename = await props.getFufExpectedDemandFileName();
        setExpectedDemandFileName(expectedDemandFilename);
    }, []);

    const disableUploadButton =
        fileProperties?.fufUri == undefined ||
        fileProperties?.gfv !== props.currentGfv ||
        uploadFufStatus === UploadedFileStatus.Unresolved;

    const haveError =
        uploadFufStatus === UploadedFileStatus.Unresolved ||
        (fileProperties?.gfv !== props.currentGfv && uploadFufStatus !== UploadedFileStatus.Cancel);

    const renderBody = (): JSX.Element => (
        <Content>
            {props.uploadFufLightboxAlert && <FufUploadAlert text={props.uploadFufLightboxAlert} />}
            {fileProperties !== undefined && (
                <FufFileProperties
                    uploadFufLightboxHideDemandNameButton={props.uploadFufLightboxHideDemandNameButton}
                    fileProperties={fileProperties}
                    expectedDemandFileName={expectedDemandFileName}
                />
            )}
            <FileUploader
                size="medium"
                data-tid="FileUploadControl"
                request={clearAndUploadFuf}
                width={"100%"}
                error={haveError}
                onRemove={onClear}
            />
            {haveError && <FufUploadError fileName={fileName} messageWithMarkup={loadError} />}
        </Content>
    );

    return (
        <Modal ignoreBackgroundClick width={660} onClose={onClose}>
            <Modal.Header>{props.uploadFufLightboxTitle ?? "Загрузка файла"}</Modal.Header>
            <Modal.Body>
                <Container>{renderBody()}</Container>
            </Modal.Body>
            <Modal.Footer panel={true} gap={8}>
                <Button
                    size="medium"
                    disabled={disableUploadButton}
                    onClick={uploadForEdit}
                    use="primary"
                    data-tid="LoadFuf"
                    loading={isLoading}>
                    Загрузить
                </Button>
                <Button size="medium" data-tid="Cancel" onClick={onClose}>
                    Отмена
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
